/*
 *
 * Tenants
 *
 */

import React from "react"
import { connect } from "react-redux"
import { setLoader, setTitle } from "store/actions"
import { Props, State, Tenant } from "./types"
import { Helmet } from "react-helmet"
import { Table } from "components"
import { Col, Input, Modal, notification, Row, Select, Tag } from "antd"
import Dropzone from "react-dropzone"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import "./styles.scss"

// @ts-ignore
const { Option } = Select
// @ts-ignore
const { TextArea } = Input

const AUTH_METHOD_OPTIONS = [
	{ value: "password", label: "Password" },
	{ value: "google", label: "Google" },
]

const AUTH_METHOD_COLORS: Record<string, string> = {
	password: "blue",
	google: "red",
}

class Tenants extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props)

		this.state = {
			tenants: [],
			showModal: false,
			isEditing: false,
			selectedTenant: null,
			name: "",
			domain: "",
			authMethods: ["password"],
			contactName: "",
			contactEmail: "",
			contactPhone: "",
			primaryColor: "#1890ff",
			secondaryColor: "#ffffff",
			notes: "",
			logo: null,
			logoPreview: "",
		}
	}

	componentDidMount() {
		const { dispatch } = this.props

		dispatch(setTitle(""))
		this.getData()
	}

	async getData() {
		const { dispatch } = this.props

		dispatch(setLoader(true))

		let response: any
		try {
			response = await API.get({
				url: Endpoints.v2.uriTenants(),
			})

			if (response.ok) {
				this.setState({ tenants: response.data.results.tenants || [] })
			}
		} catch (err) {
			notification.error({
				message: Strings.tenants.tenants,
				description: response?.data?.message || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})

			console.log("Error", err as string)
		}

		dispatch(setLoader(false))
	}

	openCreateModal() {
		this.setState({
			showModal: true,
			isEditing: false,
			selectedTenant: null,
			name: "",
			domain: "",
			authMethods: ["password"],
			contactName: "",
			contactEmail: "",
			contactPhone: "",
			primaryColor: "#1890ff",
			secondaryColor: "#ffffff",
			notes: "",
			logo: null,
			logoPreview: "",
		})
	}

	openEditModal(tenant: Tenant) {
		this.setState({
			showModal: true,
			isEditing: true,
			selectedTenant: tenant,
			name: tenant.name,
			domain: tenant.domain || "",
			authMethods: tenant.authMethods || ["password"],
			contactName: tenant.contactName || "",
			contactEmail: tenant.contactEmail || "",
			contactPhone: tenant.contactPhone || "",
			primaryColor: tenant.primaryColor || "#1890ff",
			secondaryColor: tenant.secondaryColor || "#ffffff",
			notes: tenant.notes || "",
			logo: null,
			logoPreview: tenant.logoSignedUrl || "",
		})
	}

	closeModal() {
		this.setState({
			showModal: false,
			isEditing: false,
			selectedTenant: null,
		})
	}

	onDrop(files: any[]) {
		const file = files[0]
		if (!file) return

		this.setState({
			logo: file,
			logoPreview: URL.createObjectURL(file),
		})
	}

	async handleSubmit() {
		const { dispatch } = this.props
		const {
			isEditing,
			selectedTenant,
			name,
			domain,
			authMethods,
			contactName,
			contactEmail,
			contactPhone,
			primaryColor,
			secondaryColor,
			notes,
			logo,
		} = this.state

		if (!name?.trim()) {
			notification.error({
				message: Strings.tenants.tenants,
				description: Strings.tenants.nameRequired,
				placement: "bottomRight",
				duration: 5,
			})
			return
		}

		if (!authMethods?.length) {
			notification.error({
				message: Strings.tenants.tenants,
				description: Strings.tenants.authMethodsRequired,
				placement: "bottomRight",
				duration: 5,
			})
			return
		}

		dispatch(setLoader(true))

		const formData = new FormData()
		formData.append("name", JSON.stringify(name.trim()))
		formData.append("domain", JSON.stringify(domain.trim()))
		formData.append("authMethods", JSON.stringify(authMethods))
		formData.append("contactName", JSON.stringify(contactName))
		formData.append("contactEmail", JSON.stringify(contactEmail))
		formData.append("contactPhone", JSON.stringify(contactPhone))
		formData.append("primaryColor", JSON.stringify(primaryColor))
		formData.append("secondaryColor", JSON.stringify(secondaryColor))
		formData.append("notes", JSON.stringify(notes))

		if (logo) {
			formData.append("file", logo)
		}

		let response: any
		try {
			if (isEditing && selectedTenant) {
				response = await API.put({
					url: Endpoints.v2.uriTenants(selectedTenant._id),
					data: formData,
				})
			} else {
				response = await API.post({
					url: Endpoints.v2.uriTenants(),
					data: formData,
				})
			}

			if (response.ok) {
				await this.getData()
				this.closeModal()

				notification.success({
					message: Strings.tenants.tenants,
					description: isEditing
						? Strings.tenants.edited
						: Strings.tenants.created,
					placement: "bottomRight",
					duration: 5,
				})
			} else {
				notification.error({
					message: Strings.tenants.tenants,
					description:
						response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.tenants.tenants,
				description: response?.data?.message || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})

			console.log("Error", err as string)
		}

		dispatch(setLoader(false))
	}

	async toggleTenant(tenant: Tenant) {
		const { dispatch } = this.props

		dispatch(setLoader(true))

		let response: any
		try {
			response = await API.patch({
				url: Endpoints.v2.uriTenants(tenant._id),
				data: {
					active: !tenant.active,
				},
			})

			if (response.ok) {
				await this.getData()

				notification.success({
					message: Strings.tenants.tenants,
					description: tenant.active
						? Strings.tenants.deactivated
						: Strings.tenants.activated,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.tenants.tenants,
				description: response?.data?.message || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})

			console.log("Error", err as string)
		}

		dispatch(setLoader(false))
	}

	async deleteTenant(tenant: Tenant) {
		const { dispatch } = this.props

		dispatch(setLoader(true))

		let response: any
		try {
			response = await API.delete({
				url: Endpoints.v2.uriTenants(tenant._id),
			})

			if (response.ok) {
				await this.getData()

				notification.success({
					message: Strings.tenants.tenants,
					description: Strings.tenants.deleted,
					placement: "bottomRight",
					duration: 5,
				})
			}
		} catch (err) {
			notification.error({
				message: Strings.tenants.tenants,
				description: response?.data?.message || Strings.serverErrors.wentWrong,
				placement: "bottomRight",
				duration: 5,
			})

			console.log("Error", err as string)
		}

		dispatch(setLoader(false))
	}

	renderModal() {
		const {
			showModal,
			isEditing,
			name,
			domain,
			authMethods,
			contactName,
			contactEmail,
			contactPhone,
			primaryColor,
			secondaryColor,
			notes,
			logoPreview,
		} = this.state

		return (
			<Modal
				title={isEditing ? Strings.tenants.edit : Strings.tenants.new}
				visible={showModal}
				onOk={() => this.handleSubmit()}
				onCancel={() => this.closeModal()}
				okText={Strings.generic.save}
				cancelText={Strings.generic.cancel}
				width={700}
				centered
				className="TenantModal"
			>
				<Row gutter={[20, 10]}>
					<Col xs={24} md={6}>
						<div className="TenantLogoContainer">
							<Dropzone
								accept="image/jpg, image/jpeg, image/png"
								className="TenantLogoUpload"
								onDrop={(files: any) => this.onDrop(files)}
							>
								{logoPreview ? (
									<div
										className="TenantLogoPreview"
										style={{
											backgroundImage: `url('${logoPreview}')`,
											backgroundSize: "contain",
											backgroundRepeat: "no-repeat",
											backgroundPosition: "center",
										}}
									/>
								) : (
									<div className="TenantLogoPlaceholder">
										<span>{Strings.fields.logo}</span>
									</div>
								)}
							</Dropzone>
						</div>
					</Col>
					<Col xs={24} md={18}>
						<Row gutter={[20, 10]}>
							<Col xs={24} md={12}>
								<label
									htmlFor="tenant_name"
									className="InputLabel --label-required"
								>
									{Strings.fields.name}
								</label>
								<Input
									id="tenant_name"
									value={name}
									placeholder={Strings.fields.name}
									onChange={(e) => this.setState({ name: e.target.value })}
								/>
							</Col>
							<Col xs={24} md={12}>
								<label htmlFor="tenant_domain" className="InputLabel">
									{Strings.tenants.domain}
								</label>
								<Input
									id="tenant_domain"
									value={domain}
									placeholder="example.com"
									onChange={(e) => this.setState({ domain: e.target.value })}
								/>
							</Col>
						</Row>
						<Row gutter={[20, 10]} style={{ marginTop: 10 }}>
							<Col xs={24}>
								<label
									htmlFor="tenant_auth_methods"
									className="InputLabel --label-required"
								>
									{Strings.tenants.authMethods}
								</label>
								<Select
									id="tenant_auth_methods"
									mode="multiple"
									value={authMethods}
									onChange={(value: string[]) =>
										this.setState({ authMethods: value })
									}
									style={{ width: "100%" }}
									placeholder={Strings.tenants.authMethods}
								>
									{AUTH_METHOD_OPTIONS.map((opt) => (
										<Option key={opt.value} value={opt.value}>
											{opt.label}
										</Option>
									))}
								</Select>
							</Col>
						</Row>
					</Col>
				</Row>

				<div className="ModalSectionTitle" style={{ marginTop: 20 }}>
					<span>{Strings.businesses.businessInformation}</span>
				</div>

				<Row gutter={[20, 10]}>
					<Col xs={24} md={8}>
						<label htmlFor="tenant_contact_name" className="InputLabel">
							{Strings.tenants.contactName}
						</label>
						<Input
							id="tenant_contact_name"
							value={contactName}
							placeholder={Strings.tenants.contactName}
							onChange={(e) => this.setState({ contactName: e.target.value })}
						/>
					</Col>
					<Col xs={24} md={8}>
						<label htmlFor="tenant_contact_email" className="InputLabel">
							{Strings.tenants.contactEmail}
						</label>
						<Input
							id="tenant_contact_email"
							value={contactEmail}
							placeholder={Strings.tenants.contactEmail}
							onChange={(e) => this.setState({ contactEmail: e.target.value })}
						/>
					</Col>
					<Col xs={24} md={8}>
						<label htmlFor="tenant_contact_phone" className="InputLabel">
							{Strings.tenants.contactPhone}
						</label>
						<Input
							id="tenant_contact_phone"
							value={contactPhone}
							placeholder={Strings.tenants.contactPhone}
							onChange={(e) => this.setState({ contactPhone: e.target.value })}
						/>
					</Col>
				</Row>

				<Row gutter={[20, 10]} style={{ marginTop: 10 }}>
					<Col xs={24} md={6}>
						<label htmlFor="tenant_primary_color" className="InputLabel">
							{Strings.tenants.primaryColor}
						</label>
						<div className="ColorPickerWrapper">
							<input
								type="color"
								id="tenant_primary_color"
								value={primaryColor}
								onChange={(e) =>
									this.setState({ primaryColor: e.target.value })
								}
								className="ColorPicker"
							/>
							<Input
								value={primaryColor}
								onChange={(e) =>
									this.setState({ primaryColor: e.target.value })
								}
								style={{ flex: 1 }}
							/>
						</div>
					</Col>
					<Col xs={24} md={6}>
						<label htmlFor="tenant_secondary_color" className="InputLabel">
							{Strings.tenants.secondaryColor}
						</label>
						<div className="ColorPickerWrapper">
							<input
								type="color"
								id="tenant_secondary_color"
								value={secondaryColor}
								onChange={(e) =>
									this.setState({ secondaryColor: e.target.value })
								}
								className="ColorPicker"
							/>
							<Input
								value={secondaryColor}
								onChange={(e) =>
									this.setState({ secondaryColor: e.target.value })
								}
								style={{ flex: 1 }}
							/>
						</div>
					</Col>
					<Col xs={24} md={12}>
						<label htmlFor="tenant_notes" className="InputLabel">
							{Strings.tenants.notes}
						</label>
						<TextArea
							id="tenant_notes"
							value={notes}
							placeholder={Strings.tenants.notes}
							onChange={(e: any) => this.setState({ notes: e.target.value })}
							rows={2}
						/>
					</Col>
				</Row>
			</Modal>
		)
	}

	render() {
		const { tenants } = this.state

		return (
			<React.Fragment>
				<Helmet>
					<title>{Strings.tenants.tenants}</title>
					<meta name="description" content="Tenants Management" />
				</Helmet>
				<Table
					title={{
						icon: "companhia",
						title: Strings.tenants.tenants,
					}}
					data={tenants}
					columns={[
						{
							Header: Strings.fields.logo,
							id: "logo",
							accessor: (row: any) => row.logoSignedUrl,
							type: "image",
							maxWidth: 65,
						},
						{
							Header: Strings.fields.name,
							id: "name",
							accessor: (row: any) => row.name,
						},
						{
							Header: Strings.tenants.domain,
							id: "domain",
							accessor: (row: any) => row.domain || "-",
						},
						{
							Header: Strings.tenants.authMethods,
							id: "authMethods",
							accessor: (row: any) => row.authMethods,
							Cell: (row: any) => (
								<div>
									{(row.value || []).map((method: string) => (
										<Tag
											key={method}
											color={AUTH_METHOD_COLORS[method] || "default"}
										>
											{method}
										</Tag>
									))}
								</div>
							),
						},
						{
							Header: Strings.tenants.database,
							id: "database",
							accessor: (row: any) => row.database,
						},
					]}
					filterable
					fullPage
					isSinglePage
					sortable
					add={{
						onClick: () => this.openCreateModal(),
					}}
					actions={{
						edit: (original: any) => ({
							onClick: () => this.openEditModal(original),
						}),
						toggle: (original: any) => ({
							value: original.active,
							onChange: () => this.toggleTenant(original),
						}),
						...(process.env.ENVIRONMENT !== "prod" &&
							process.env.ENVIRONMENT !== "stag" && {
								deleteAction: (original: any) => ({
									onClick: () => this.deleteTenant(original),
								}),
							}),
					}}
				/>
				{this.renderModal()}
			</React.Fragment>
		)
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
})

export default connect(mapStateToProps)(Tenants)
